/**
 * useDocumentChannel
 *
 * Subscribes to the Ably document-processing channel for a given project.
 * Mirrors the web's useDocumentUploadChannel hook.
 *
 * Backend publishes to:
 *   channel:  documents_{stage}_{account_id}_{cognito_id}_{project_id}
 *   event:    "Document Upload {project_id}"
 *
 * Payload shape: { id, status, filename, kb_access, failure_code?, ... }
 *   status === 'active'   → processing complete (success)
 *   failure_code present  → processing failed
 */
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAblyClient } from '../contexts/AblyContext';
import config from '../config';
import debug from '../utilities/debug';
import type { RootState } from '../store';

interface Options {
  projectId: number | string | null | undefined;
  /** Called with the document payload on every Ably message for this project. */
  onMessage: (doc: any) => void;
  /**
   * Called when the channel (re)attaches. Use it to refetch documents — Ably
   * does not redeliver messages published while the client was detached or
   * disconnected (beyond the single rewound message), so a reconcile on attach
   * is what guarantees a doc never stays stuck on "processing".
   */
  onReconcile?: () => void;
}

export function useDocumentChannel({ projectId, onMessage, onReconcile }: Options) {
  const { client } = useAblyClient();
  const account = useSelector((s: RootState) => (s as any).session.account);
  const user = useSelector((s: RootState) => s.authentication.user);

  // Keep stable refs so the effect doesn't re-subscribe when callback identities change
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const onReconcileRef = useRef(onReconcile);
  onReconcileRef.current = onReconcile;

  useEffect(() => {
    if (!client || !account?.account_id || !user?.cognito_id || projectId == null) return;

    const channelName = `documents_${config.env}_${account.account_id}_${user.cognito_id}_${projectId}`;
    const eventName = `Document Upload ${projectId}`;

    let channel: any;
    const handler = (message: any) => {
      const doc = message?.data;
      if (doc) onMessageRef.current(doc);
    };
    const onAttached = () => {
      debug.log('ably', 'Document channel attached', { channelName });
      // Reconcile on (re)attach to catch anything published while we were
      // detached/disconnected — Ably doesn't redeliver those beyond rewind.
      try { onReconcileRef.current?.(); } catch {}
    };
    const onChannelError = (stateChange: any) => {
      debug.warn('Document channel error', { channelName, reason: stateChange?.reason?.message ?? stateChange?.reason ?? null });
    };

    try {
      // rewind:1 replays the most recent message on attach, so a subscriber
      // that attaches just AFTER the processor published still receives the
      // completion event instead of missing it — the "stuck on processing" bug.
      channel = client.channels.get(channelName, { params: { rewind: '1' } });
      channel.on('attached', onAttached);
      channel.on('failed', onChannelError);
      channel.on('suspended', onChannelError);
      channel.subscribe(eventName, handler);
    } catch (err) {
      // Previously swallowed silently — log so a real subscribe failure is visible.
      debug.warn('Document channel subscribe failed', { channelName, err: (err as any)?.message });
    }

    return () => {
      try {
        channel?.unsubscribe(eventName, handler);
        channel?.off('attached', onAttached);
        channel?.off('failed', onChannelError);
        channel?.off('suspended', onChannelError);
      } catch {}
    };
  // projectId is intentionally stringified to avoid reference inequality on number vs string
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, account?.account_id, user?.cognito_id, String(projectId)]);
}
